import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import FeatureFlagGuard from 'core/auth/feature-flag-guard.component';
import ProtectedRoute from 'core/auth/protected-route.component';
import { stencilNavigationDataStrategy } from 'core/stencil';

import {
    DEVICE_MANAGEMENT_FEATURE_FLAG,
    MATERIAL_MANAGEMENT_FEATURE_FLAG,
} from 'core/service/feature-flag/feature-flag.constants';
import RoleGuard from 'core/auth/role-guard.component';
import { appRoles } from 'core/constants/constants';
import { lotsDetailRoutes, lotsRedirectRoutes, lotsSidebar } from 'modules/lots/lots.router';
import { SettingsLayoutComponent } from 'modules/settings/settings.layout';
import React from 'react';
import SettingsActiveDirectoryDetailsScene from 'scenes/settings/settings-active-directory-details.scene';
import SettingsActiveDirectoryListScene from 'scenes/settings/settings-active-directory-list.scene';
import SettingsAutomationRulesScene from 'scenes/settings/settings-automation-rules.scene';
import SettingsCustomFieldsScene from 'scenes/settings/settings-custom-fields.scene';
import { SettingsDeviceTypesScene } from 'scenes/settings/settings-device-type.scene';
import SettingsDevicesConfigurationLayoutScene from 'scenes/settings/settings-devices-configuration-layout.scene';
import SettingsDevicesConfigurationScene from 'scenes/settings/settings-devices-configuration.scene';
import SettingsDevicesCustomAlarmsFormScene from 'scenes/settings/settings-devices-custom-alarms-form.scene';
import SettingsDevicesEapSettingsFormScene from 'scenes/settings/settings-devices-eap-settings-create-form.scene';
import SettingsDevicesEapSettingsImportingScene from 'scenes/settings/settings-devices-eap-settings-importing.scene';
import SettingsDevicesEapSettingsScene from 'scenes/settings/settings-devices-eap-settings.scene';
import SettingsDevicesPredefinedCommentsFormScene from 'scenes/settings/settings-devices-predefined-comments-form.scene';
import SettingsDevicesPredefinedCommentsScene from 'scenes/settings/settings-devices-predefined-comments.scene';
import SettingsLmsApiIntegrationScene from 'scenes/settings/settings-lms-api-integration.scene';
import SettingsMaterialDefinitionsFormScene from 'scenes/settings/settings-material-definitions-form.scene';
import SettingsMaterialDefinitionsScene from 'scenes/settings/settings-material-definitions.scene';
import SettingsOperatorCustomNotificationFormScene from 'scenes/settings/settings-operator-custom-notification-form.scene';
import SettingsOperatorCustomNotificationsScene from 'scenes/settings/settings-operator-custom-notifications.scene';
import SettingsTrainingSettingsScene from 'scenes/settings/settings-training-settings.scene';
import SettingsUserLocationAssignmentScene from 'scenes/settings/settings-user-location-assignment.scene';
import { linkRoutes, settingsModulePaths, switchRoutes } from './core/routes/routes';
import { AppLayout } from './layout';

const OperatorsScene = React.lazy(() => import('scenes/operators/operators.scene'));
const OperatorDetailsScene = React.lazy(() => import('scenes/operator-details/operator-details.scene'));
const TrainingsScene = React.lazy(() => import('scenes/trainings/trainings.scene'));
const TrainingDetailsScene = React.lazy(() => import('scenes/training-details/training-details.scene'));
const DevicesScene = React.lazy(() => import('scenes/devices/devices.scene'));
const DeviceDetailsScene = React.lazy(() => import('scenes/device-details/device-details.scene'));
const ErrorScene = React.lazy(() => import('scenes/error/error.scene'));

export const routes = [
    {
        path: switchRoutes.root,
        element: (
            <ProtectedRoute>
                <AppLayout />
            </ProtectedRoute>
        ),
        errorElement: <ErrorScene />,
        children: [
            {
                index: true,
                element: <Navigate to={switchRoutes.operators} replace />,
            },
            {
                path: switchRoutes.operators,
                element: <OperatorsScene />,
            },
            {
                path: `${switchRoutes.operators}/filters/:filter`,
                element: <OperatorsScene />,
            },
            {
                path: `${switchRoutes.operators}/:operatorId`,
                element: <OperatorDetailsScene />,
            },
            {
                path: switchRoutes.trainings,
                element: <TrainingsScene />,
            },
            {
                path: `${switchRoutes.trainings}/:trainingProgramId/stages/:trainingStageId`,
                element: <TrainingDetailsScene />,
            },
            {
                path: switchRoutes.settings,
                element: <SettingsLayoutComponent />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="automation-rules" replace />,
                    },
                    {
                        path: settingsModulePaths.automationRules,
                        element: <SettingsAutomationRulesScene />,
                    },
                    {
                        path: settingsModulePaths.activeDirectoryList,
                        element: <SettingsActiveDirectoryListScene />,
                    },
                    {
                        path: settingsModulePaths.activeDirectoryDetails,
                        element: <SettingsActiveDirectoryDetailsScene />,
                    },
                    {
                        path: settingsModulePaths.customFields,
                        element: <SettingsCustomFieldsScene />,
                    },
                    {
                        path: settingsModulePaths.operatorCustomNotifications,
                        element: <SettingsOperatorCustomNotificationsScene />,
                    },
                    {
                        path: settingsModulePaths.operatorCustomNotificationForm,
                        element: <SettingsOperatorCustomNotificationFormScene />,
                    },
                    {
                        path: settingsModulePaths.trainingSettings,
                        element: <SettingsTrainingSettingsScene />,
                    },
                    {
                        path: settingsModulePaths.lmsIntegration,
                        element: <SettingsLmsApiIntegrationScene />,
                    },
                    {
                        path: settingsModulePaths.deviceTypes,
                        element: <SettingsDeviceTypesScene />,
                    },
                    {
                        path: settingsModulePaths.eapSettings,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesEapSettingsScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.eapSettingsImporting,
                        element: (
                            <FeatureFlagGuard featureFlag="deviceManagementEnabled" redirect={linkRoutes.settings}>
                                <SettingsDevicesEapSettingsImportingScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.eapSettingsForm,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesEapSettingsFormScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.predefinedComments,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesPredefinedCommentsScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.predefinedCommentsForm,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesPredefinedCommentsFormScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.deviceConfiguration,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesConfigurationScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.deviceConfigurationDetail,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesConfigurationLayoutScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.deviceConfigurationCustomAlarmForm,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsDevicesCustomAlarmsFormScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.materialDefinitionsForm,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={MATERIAL_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsMaterialDefinitionsFormScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.materialDefinitions,
                        element: (
                            <FeatureFlagGuard
                                featureFlag={MATERIAL_MANAGEMENT_FEATURE_FLAG}
                                redirect={linkRoutes.settings}
                            >
                                <SettingsMaterialDefinitionsScene />
                            </FeatureFlagGuard>
                        ),
                    },
                    {
                        path: settingsModulePaths.userLocationAssignment,
                        element: (
                            <RoleGuard allowedRole={appRoles.POCC_ADMIN} forbidden={<ErrorScene />}>
                                <SettingsUserLocationAssignmentScene />
                            </RoleGuard>
                        ),
                    },
                ],
            },
            {
                path: switchRoutes.devices,
                element: (
                    <FeatureFlagGuard featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG} redirect={linkRoutes.root}>
                        <DevicesScene key="devices-root" />
                    </FeatureFlagGuard>
                ),
            },
            {
                path: `${switchRoutes.devices}/filters/:filter`,
                element: (
                    <FeatureFlagGuard featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG} redirect={linkRoutes.root}>
                        <DevicesScene key="devices-filter" />
                    </FeatureFlagGuard>
                ),
            },
            {
                path: switchRoutes.deviceDetail,
                element: (
                    <FeatureFlagGuard featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG} redirect={linkRoutes.root}>
                        <DeviceDetailsScene />
                    </FeatureFlagGuard>
                ),
            },
            ...lotsSidebar,
            ...lotsDetailRoutes,
            ...lotsRedirectRoutes,
            {
                path: '*',
                element: <ErrorScene />,
            },
        ],
    },
];

const router = createBrowserRouter(routes, { dataStrategy: stencilNavigationDataStrategy });

export const AppRouter: React.FC = () => <RouterProvider router={router} />;
